import { INPUT_BAR_COMPACT_SURFACE_CLASSES } from "@app/components/assistant/conversation/input_bar/inputBarCompactStyles";
import type { SidebarNavigation } from "@app/components/navigation/config";
import { NavigationRail } from "@app/components/navigation/NavigationRail";
import { NavigationSidebar } from "@app/components/navigation/NavigationSidebar";
import { SidebarContext } from "@app/components/sparkle/SidebarContext";
import { useUser } from "@app/lib/swr/user";
import type { SubscriptionType } from "@app/types/plan";
import type { WorkspaceType } from "@app/types/user";
import {
  Button,
  cn,
  Menu01,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@dust-tt/sparkle";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import type React from "react";
import { useContext } from "react";

const MOBILE_NAV_MENU_BUTTON_CLASSES = cn(
  INPUT_BAR_COMPACT_SURFACE_CLASSES,
  "transition-none",
  "hover:border-transparent hover:bg-hover hover:backdrop-blur-none",
  "active:border-transparent active:bg-primary-300 active:backdrop-blur-none"
);

interface NavigationProps {
  hideSidebar: boolean;
  owner: WorkspaceType;
  subscription: SubscriptionType;
  navChildren?: React.ReactNode;
  subNavigation?: SidebarNavigation[] | null;
  isNavigationBarOpen: boolean;
  setNavigationBarOpen: (isOpen: boolean) => void;
  isFullScreen: boolean;
  isMobile: boolean;
}

export function Navigation({
  hideSidebar,
  owner,
  subscription,
  navChildren,
  subNavigation,
  isNavigationBarOpen,
  setNavigationBarOpen,
  isFullScreen,
  isMobile,
}: NavigationProps) {
  const { sidebarOpen, setSidebarOpen } = useContext(SidebarContext);

  const { user } = useUser();

  if (hideSidebar) {
    return null;
  }

  return (
    <div
      className={cn(
        "flex h-full shrink-0 overflow-x-hidden",
        "text-primary",
        "bg-app-background"
      )}
    >
      {isMobile ? (
        <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
          <div className="fixed left-0 top-0 z-40 flex shrink-0 items-center px-2 pt-2">
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                icon={Menu01}
                className={MOBILE_NAV_MENU_BUTTON_CLASSES}
                onClick={() => setSidebarOpen(true)}
                aria-label="Open navigation"
              />
            </SheetTrigger>
          </div>
          <SheetContent
            side="left"
            className={cn(
              "flex flex-1 flex-row bg-app-background",
              isNavigationBarOpen ? "w-full max-w-xs" : "w-auto"
            )}
          >
            <SheetHeader className="bg-app-background p-0" hideButton={true}>
              <VisuallyHidden>
                <SheetTitle className="hidden" />
              </VisuallyHidden>
            </SheetHeader>
            <NavigationRail
              owner={owner}
              subscription={subscription}
              user={user}
            />
            {isNavigationBarOpen && (
              <div className="flex min-w-0 flex-1 border-l border-border">
                <NavigationSidebar
                  subscription={subscription}
                  owner={owner}
                  subNavigation={subNavigation}
                  user={user}
                  isMobile={true}
                >
                  {navChildren && navChildren}
                </NavigationSidebar>
              </div>
            )}
          </SheetContent>
        </Sheet>
      ) : (
        !isFullScreen && (
          <>
            <NavigationRail
              owner={owner}
              subscription={subscription}
              user={user}
            />
            <div
              className={cn(
                "flex h-full flex-none flex-col overflow-hidden",
                "transition-[width] duration-300 ease-in-out motion-reduce:transition-none",
                isNavigationBarOpen ? "mr-2 w-72" : "w-0"
              )}
            >
              <div
                className={cn(
                  "flex h-full min-h-0 w-72 flex-col border-x border-border bg-app-background",
                  "transition-opacity duration-200 ease-out motion-reduce:transition-none",
                  isNavigationBarOpen ? "opacity-100" : "opacity-0"
                )}
              >
                <NavigationSidebar
                  owner={owner}
                  subscription={subscription}
                  subNavigation={subNavigation}
                  user={user}
                >
                  {navChildren && navChildren}
                </NavigationSidebar>
              </div>
            </div>
          </>
        )
      )}
    </div>
  );
}
